import { sha256 } from '@noble/hashes/sha256';

/** RWAX 账户地址前缀（解析时亦接受小写 `r`；过渡期接受遗留 `D`/`C`）。 */
export const CPC_ADDRESS_PREFIX = 'R';
/** 遗留 DGC 前缀（仅解析）。 */
export const CPC_ADDRESS_PREFIX_LEGACY_DGC = 'D';
/** 更早遗留前缀（仅解析）。 */
export const CPC_ADDRESS_PREFIX_LEGACY = 'C';

/** 主网账户地址版本字节。 */
export const CPC_ADDRESS_VERSION = 0x01;

const B58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

function sha256d(data: Uint8Array): Uint8Array {
	return sha256(sha256(data));
}

function base58Encode(bytes: Uint8Array): string {
	let zeros = 0;
	while (zeros < bytes.length && bytes[zeros] === 0) zeros++;
	const size = Math.ceil(bytes.length * 1.365) + 1;
	const b = new Uint8Array(size);
	let length = 0;
	for (let i = zeros; i < bytes.length; i++) {
		let carry = bytes[i];
		let j = 0;
		for (let k = size - 1; (carry !== 0 || j < length) && k >= 0; k--, j++) {
			carry += 256 * b[k];
			b[k] = carry % 58;
			carry = (carry / 58) | 0;
		}
		length = j;
	}
	let it = size - length;
	while (it < size && b[it] === 0) it++;
	let str = '1'.repeat(zeros);
	for (; it < size; it++) str += B58_ALPHABET[b[it]];
	return str;
}

function base58Decode(str: string): Uint8Array {
	let zeros = 0;
	while (zeros < str.length && str[zeros] === '1') zeros++;
	const size = Math.ceil(str.length * 0.733) + 1;
	const b = new Uint8Array(size);
	let length = 0;
	for (let i = zeros; i < str.length; i++) {
		const ch = B58_ALPHABET.indexOf(str[i]);
		if (ch < 0) throw new Error('invalid base58');
		let carry = ch;
		let j = 0;
		for (let k = size - 1; (carry !== 0 || j < length) && k >= 0; k--, j++) {
			carry += 58 * b[k];
			b[k] = carry % 256;
			carry = (carry / 256) | 0;
		}
		length = j;
	}
	let it = size - length;
	while (it < size && b[it] === 0) it++;
	const out = new Uint8Array(zeros + (size - it));
	out.fill(0, 0, zeros);
	let p = zeros;
	while (it < size) out[p++] = b[it++];
	return out;
}

function hexToBytes(hex: string): Uint8Array {
	const h = hex.length % 2 === 0 ? hex : `0${hex}`;
	const out = new Uint8Array(h.length / 2);
	for (let i = 0; i < out.length; i++) {
		out[i] = parseInt(h.slice(i * 2, i * 2 + 2), 16);
	}
	return out;
}

function bytesToHex(bytes: Uint8Array): string {
	return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function looksLikeCpcDisplayAddress(input: string): boolean {
	const s = input.trim();
	return (
		s.startsWith(CPC_ADDRESS_PREFIX) ||
		s.startsWith('r') ||
		s.startsWith(CPC_ADDRESS_PREFIX_LEGACY_DGC) ||
		s.startsWith('d') ||
		s.startsWith(CPC_ADDRESS_PREFIX_LEGACY) ||
		s.startsWith('c')
	);
}

/** 32 字节 → `D…` */
export function encodeCpcAddress(addressBytes: Uint8Array): string {
	if (addressBytes.length !== 32) {
		throw new Error('RWAX address must be 32 bytes');
	}
	const payload = new Uint8Array(33);
	payload[0] = CPC_ADDRESS_VERSION;
	payload.set(addressBytes, 1);
	const checksum = sha256d(payload).slice(0, 4);
	const raw = new Uint8Array(37);
	raw.set(payload, 0);
	raw.set(checksum, 33);
	return `${CPC_ADDRESS_PREFIX}${base58Encode(raw)}`;
}

/** `D…` / 遗留 `C…` → 32 字节 */
export function decodeCpcAddress(input: string): Uint8Array {
	const s = input.trim();
	if (!looksLikeCpcDisplayAddress(s) || s.length < 2) {
		throw new Error('invalid RWAX address prefix');
	}
	const body = s.slice(1);
	if (body.length === 0) {
		throw new Error('invalid RWAX address prefix');
	}
	const raw = base58Decode(body);
	if (raw.length !== 37) {
		throw new Error('invalid RWAX address length');
	}
	const payload = raw.slice(0, 33);
	const checksum = raw.slice(33);
	const expect = sha256d(payload).slice(0, 4);
	if (checksum.some((b, i) => b !== expect[i])) {
		throw new Error('invalid RWAX address checksum');
	}
	if (payload[0] !== CPC_ADDRESS_VERSION) {
		throw new Error(`unsupported RWAX address version: ${payload[0]}`);
	}
	return payload.slice(1);
}

/**
 * 解析用户输入：`D…`（及遗留 `C…`）/ `0x`+64 hex / 裸 64 hex → 32 字节。
 * 短 hex（如 `0x2`）请走链上类型路径，不经此函数。
 */
export function parseCpcAddressInput(input: string): Uint8Array {
	const s = input.trim();
	if (!s) throw new Error('empty address');
	if (looksLikeCpcDisplayAddress(s)) {
		return decodeCpcAddress(s);
	}
	let hex = s.startsWith('0x') || s.startsWith('0X') ? s.slice(2) : s;
	// Wallet / dapp-kit may omit leading zeros. Do not pad short ids like `0x2`.
	if (/^[0-9a-fA-F]{40,64}$/.test(hex)) {
		hex = hex.padStart(64, '0');
	}
	if (!/^[0-9a-fA-F]{64}$/.test(hex)) {
		throw new Error('invalid hex address');
	}
	return hexToBytes(hex);
}

/** → `0x` + 小写 64 hex（RPC） */
export function normalizeToHexAddress(input: string): string {
	return `0x${bytesToHex(parseCpcAddressInput(input))}`;
}

/** → `R…` 展示串 */
export function formatCpcAddress(input: string): string {
	return encodeCpcAddress(parseCpcAddressInput(input));
}

/** Best-effort display: `R…` on success, else original trimmed input. */
export function tryFormatCpcAddress(input: string): string {
	const s = input?.trim?.() ?? '';
	if (!s) return s;
	try {
		return formatCpcAddress(s);
	} catch {
		return s;
	}
}

/** Best-effort RPC hex; returns null if input is not a 32-byte account address. */
export function tryNormalizeToHexAddress(input: string): string | null {
	const s = input?.trim?.() ?? '';
	if (!s) return null;
	try {
		return normalizeToHexAddress(s);
	} catch {
		return null;
	}
}

/** 是否像 RWAX 展示地址或 32 字节 hex 账户地址 */
export function isCpcAccountAddress(input: string): boolean {
	try {
		parseCpcAddressInput(input);
		return true;
	} catch {
		return false;
	}
}
